import { PropTypes } from 'react';
import moment from 'moment';

import {
  getAvailableTicketPrices,
  getNextAvailableTicketDate,
  getRacesTicketAvailability,
  racesHaveTickets,
  racesHaveAvailableCharityTickets,
} from './helpers';

import { AVAILABLE, COMING_SOON, ENTRY_CLOSED, SOLD_OUT } from './helpers/availabilityTypes';

import BookNow from './component';

/**
 * Determine the button label based on tickets within races.
 */
const getButtonLabel = (availabilityType, startDate) => {
  if (moment.utc(startDate) < moment.utc()) {
    return 'Event passed';
  }

  switch (availabilityType) {
    case SOLD_OUT:
      return 'Sold out';
    case ENTRY_CLOSED:
      return 'Entry Closed';
    case COMING_SOON:
      return 'Coming Soon';
    case AVAILABLE:
    default:
      return 'Enter';
  }
};

/**
 * Determine the tickets price(s) based on races.
 */
const getPriceLabel = (races) => {
  const ticketPrices = getAvailableTicketPrices(races);
  let priceLabel = ticketPrices
    ? `Tickets ${ticketPrices.fromFormatted}${
        ticketPrices.toFormatted ? `–${ticketPrices.toFormatted}` : ''
      }`
    : '';
  const hasCharityTickets = racesHaveAvailableCharityTickets(races);
  if (hasCharityTickets) {
    if (ticketPrices) {
      priceLabel += ', charity places available';
    } else {
      priceLabel += 'Charity places available';
    }
  }
  return priceLabel;
};

/**
 * Determine the title based on races availability and start date.
 */
const getTitleLabel = (availabilityType, startDate, hasTickets) => {
  if (moment.utc(startDate) < moment.utc()) {
    return 'This event has passed';
  }

  if (!hasTickets) {
    return 'No ticket info available';
  }

  switch (availabilityType) {
    case SOLD_OUT:
      return 'This event has sold out';
    case ENTRY_CLOSED:
      return 'Entry for this event has closed';
    case COMING_SOON:
      return 'Tickets coming soon';
    case AVAILABLE:
    default:
      return 'Secure your place';
  }
};

/**
 * Container to determine presentational state for component.
 */
const BookNowContainer = ({
  organiser,
  permalink,
  races: { bookingPartner, races } = {},
  source,
  startDate,
}) => {
  const availability = getRacesTicketAvailability(races).type;
  const hasTickets = racesHaveTickets(races);
  return BookNow({
    // Tickets
    hasTickets,
    availability,
    bookingPartner,

    // UI
    titleLabel: getTitleLabel(availability, startDate, hasTickets),
    buttonLabel: getButtonLabel(availability, startDate),
    priceLabel: getPriceLabel(races),
    ticketAvailableDate: moment.utc(getNextAvailableTicketDate(races)).format('DD MMM YY'),

    // Event info
    eventWebsite: races && races[0].eventDetails && races[0].eventDetails.eventUrl,
    raceSchemaType: races[0].schemaType,
    organiser,
    permalink,
    source,
  });
};

BookNowContainer.propTypes = {
  permalink: PropTypes.string.isRequired,
  races: PropTypes.shape({
    bookingPartner: PropTypes.shape(),
    races: PropTypes.arrayOf(
      PropTypes.shape({
        eventDetails: PropTypes.shape({
          eventUrl: PropTypes.string,
        }),
      }),
    ).isRequired,
  }),
  source: PropTypes.string,
};

BookNowContainer.defaultProps = {
  races: undefined,
  source: undefined,
};

export default BookNowContainer;



// WEBPACK FOOTER //
// ./app/src/components/EventHeader/BookNow/container.js